import { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useUniverseSecurityItems } from '@biasafe.queries/securities-universes-security-items/securities-universes-security-items';
import type { Security } from '@biasafe.models';
import { useSmartBetaStrategyStore } from '@/features/Strategies/stores/definitionFormStore';
import { universeSecuritiesQuery } from '@/shared/config/queryDefaults';
import { useUniverseAsOfDate } from '../../../RebalancingSchedule/hooks/useLatestReconstitutionDate';

export function useEsgMandateScreens() {
  const {
    blockedList,
    esgMin,
    portfolioId,
    setSingleStockRestriction,
    setEsgFilters,
  } = useSmartBetaStrategyStore(
    useShallow((state) => ({
      blockedList: state.data_processor.single_stock_restriction.blocked,
      esgMin: state.data_processor.esg_filters.min,
      portfolioId: state.data_processor.universe.portfolio_id,
      setSingleStockRestriction: state.setSingleStockRestriction,
      setEsgFilters: state.setEsgFilters,
    })),
  );

  // Step 2's latest reconstitution date. The universe is resolved AT that
  // date so the eligible set and the portfolio it will rebalance are counted
  // at the same instant; it is also part of the queryKey, so changing the
  // schedule refetches instead of serving the previous date from cache.
  const asOf = useUniverseAsOfDate();
  const { data: securitiesData } = useUniverseSecurityItems(
    asOf ? { as_of: asOf } : undefined,
    {
      query: {
        ...universeSecuritiesQuery(asOf),
      },
    },
  );

  const selectedSymbols = useMemo(() => new Set(blockedList), [blockedList]);

  // Layer 1 — scope the exclusion catalog to the SELECTED universe's
  // constituents (Security.portfolioIds ∋ the chosen Morningstar portfolio id),
  // mirroring useFilteredSecurities' Layer 1. No universe picked yet ⇒ fall back
  // to the full catalog. This is what stops a US-only index from surfacing
  // global names in the blacklist search.
  const universeSecurities = useMemo(
    () =>
      portfolioId
        ? (securitiesData?.filter((s: Security) =>
            s.portfolioIds.includes(portfolioId),
          ) ?? [])
        : (securitiesData ?? []),
    [securitiesData, portfolioId],
  );

  // Identity is the ENGINE's performance_id (what single_exclusions submits and
  // the backtest keys on); a custom/unknown entry with no perf id falls back to
  // its ticker. Chips still DISPLAY the ticker — only the stored identity changes.
  const secId = (s: Security) => s.performanceId ?? s.ticker;

  const availableSecurities = useMemo(
    () =>
      universeSecurities.filter((s: Security) => !selectedSymbols.has(secId(s))),
    [universeSecurities, selectedSymbols],
  );

  const blockedSecurities = useMemo(
    () =>
      securitiesData?.filter((s: Security) => blockedList.includes(secId(s))) ??
      [],
    [securitiesData, blockedList],
  );

  const addToBlocked = useCallback(
    (symbol: string) =>
      setSingleStockRestriction({ blocked: [...blockedList, symbol] }),
    [blockedList, setSingleStockRestriction],
  );

  const removeFromBlocked = useCallback(
    (symbol: string) =>
      setSingleStockRestriction({
        blocked: blockedList.filter((s) => s !== symbol),
      }),
    [blockedList, setSingleStockRestriction],
  );

  const setEsgMin = useCallback(
    (min: number) => setEsgFilters({ min }),
    [setEsgFilters],
  );

  const esgExcludedCount = useMemo(() => {
    if (esgMin <= 0) return 0;
    return Math.round((esgMin / 100) * (securitiesData?.length ?? 0));
  }, [esgMin, securitiesData]);

  return {
    blockedList,
    esgMin,
    availableSecurities,
    blockedSecurities,
    addToBlocked,
    removeFromBlocked,
    setEsgMin,
    esgExcludedCount,
  };
}
